import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Lock, LogOut, Search, Download, Inbox, Loader2, RefreshCw, Mail, Phone } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mabm_admin_token";

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadEnquiries = async (tok = token) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${tok}` } });
      setEnquiries(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast({ title: "Session expired", description: "Please log in again." });
      } else {
        toast({ title: "Failed to load", description: "Could not fetch enquiries." });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadEnquiries(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { passcode });
      const tok = res.data.token;
      localStorage.setItem(TOKEN_KEY, tok);
      setToken(tok);
      setPasscode("");
      loadEnquiries(tok);
      toast({ title: "Welcome back", description: "You're logged in to the Leads Dashboard." });
    } catch (err) {
      toast({ title: "Login failed", description: "Incorrect passcode. Please try again." });
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.name, e.email, e.phone, e.message].filter(Boolean).some((f) => f.toLowerCase().includes(q))
    );
  }, [enquiries, query]);

  const exportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Message", "Newsletter", "Received"];
    const rows = filtered.map((e) => [
      e.name, e.email, e.phone, (e.message || "").replace(/\n/g, " "),
      e.subscribe ? "Yes" : "No",
      new Date(e.created_at).toLocaleString(),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mabm-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-secondary px-6 py-20">
        <Toaster />
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-border p-8">
          <div className="w-14 h-14 rounded-xl brand-gradient flex items-center justify-center mx-auto mb-5">
            <Lock size={24} className="text-gold-light" />
          </div>
          <h1 className="font-heading font-extrabold text-navy text-2xl text-center">Leads Dashboard</h1>
          <p className="text-sm text-muted-foreground text-center mt-2 mb-7">
            Enter your admin passcode to view website enquiries.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Admin passcode"
              className="w-full rounded-md border border-border bg-white px-4 py-3 text-sm outline-none focus:border-navy-light focus:ring-2 focus:ring-navy-light/20 transition"
            />
            <button
              type="submit"
              disabled={loggingIn}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy py-3.5 text-sm font-bold text-white hover:bg-navy-light transition-colors disabled:opacity-60"
            >
              {loggingIn ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="bg-secondary min-h-[70vh] py-10">
      <Toaster />
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-navy text-3xl">Leads Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {enquiries.length} total enquiry{enquiries.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => loadEnquiries()} className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:bg-secondary transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCSV} className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2.5 text-sm font-bold text-navy-dark hover:bg-gold-light transition-colors">
              <Download size={15} /> Export CSV
            </button>
            <button onClick={handleLogout} className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:bg-secondary transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        <div className="relative mb-6 max-w-md">
          <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone or message..."
            className="w-full rounded-md border border-border bg-white pl-10 pr-4 py-3 text-sm outline-none focus:border-navy-light focus:ring-2 focus:ring-navy-light/20 transition"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border py-20 text-center">
            <Inbox size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{query ? "No enquiries match your search." : "No enquiries yet."}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block bg-white rounded-2xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-navy text-white text-left">
                    <th className="px-5 py-4 font-semibold">Name</th>
                    <th className="px-5 py-4 font-semibold">Email</th>
                    <th className="px-5 py-4 font-semibold">Phone</th>
                    <th className="px-5 py-4 font-semibold">Message</th>
                    <th className="px-5 py-4 font-semibold">News</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, i) => (
                    <tr key={e.id} className={i % 2 ? "bg-secondary/40" : "bg-white"}>
                      <td className="px-5 py-4 font-semibold text-navy">{e.name}</td>
                      <td className="px-5 py-4"><a href={`mailto:${e.email}`} className="text-navy-light hover:underline">{e.email}</a></td>
                      <td className="px-5 py-4"><a href={`tel:${e.phone}`} className="text-navy-light hover:underline">{e.phone}</a></td>
                      <td className="px-5 py-4 text-muted-foreground max-w-xs">{e.message || <span className="italic opacity-60">—</span>}</td>
                      <td className="px-5 py-4">{e.subscribe ? <span className="rounded-full bg-gold/20 text-gold-dark px-2 py-0.5 text-xs font-bold">Yes</span> : <span className="text-muted-foreground text-xs">No</span>}</td>
                      <td className="px-5 py-4 text-muted-foreground whitespace-nowrap text-xs">{new Date(e.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden space-y-4">
              {filtered.map((e) => (
                <div key={e.id} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-bold text-navy">{e.name}</span>
                    {e.subscribe && <span className="rounded-full bg-gold/20 text-gold-dark px-2 py-0.5 text-xs font-bold">Subscribed</span>}
                  </div>
                  <div className="space-y-2 text-sm">
                    <a href={`mailto:${e.email}`} className="flex items-center gap-2 text-navy-light"><Mail size={14} /> {e.email}</a>
                    <a href={`tel:${e.phone}`} className="flex items-center gap-2 text-navy-light"><Phone size={14} /> {e.phone}</a>
                    {e.message && <p className="text-muted-foreground pt-1">{e.message}</p>}
                    <p className="text-xs text-muted-foreground pt-1">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
